/**
 * CatalogRail — designer-facing SKU picker on /design.
 *
 * Lists every SKU from the project graph that's NOT already placed in
 * the currently-selected room. Click a card to add it to that room
 * (creates a typed IN_ROOM edge via lib/userPlacements). Photo is the
 * ontology-typed SKU.photoUrl when present, falls back to category
 * generic via photoForSkuNode.
 *
 * Why click-to-add first: it's the functional ask ("add products from
 * catalog"); native drag-drop via @dnd-kit is the polish on top —
 * scheduled as a follow-up commit since the data path is identical.
 */
import { useMemo } from "react";
import { Plus, Tag } from "lucide-react";
import type { GraphNode, ProjectGraph } from "../../types/ontology";
import { photoForSkuNode } from "../../data/imagery";
import { formatCompactMoney } from "../../lib/i18n/format";
import { wouldFit, type FitVerdict } from "../../lib/skuFitCheck";

interface Props {
  readonly graph: ProjectGraph;
  readonly selectedRoomId: string | null;
  /** Agentic add — every click hands the candidate + the agent's verdict
   *  up. The caller decides whether to PLACE (verdict.ok) or REJECT
   *  (!verdict.ok) and emits the typed Suggestion either way. The rail
   *  itself no longer disables the click — the agent does the mediating. */
  readonly onAdd: (sku: GraphNode, roomId: string, verdict: FitVerdict) => void;
}

function attrNum(n: GraphNode, key: string): number | null {
  const v = n.attrs[key];
  if (typeof v === "number" && Number.isFinite(v)) return v;
  return null;
}

export function CatalogRail({ graph, selectedRoomId, onAdd }: Props): JSX.Element {
  const skus = useMemo(() => graph.nodes.filter((n) => n.kind === "SKU"), [graph.nodes]);

  // SKUs already placed in the selected room — we surface them as "added"
  // and disable the add button (idempotent click; UX rule 8 — honest state).
  const placedInSelected = useMemo<ReadonlySet<string>>(() => {
    if (!selectedRoomId) return new Set();
    return new Set(
      graph.edges
        .filter((e) => e.kind === "IN_ROOM" && e.target === selectedRoomId)
        .map((e) => e.source),
    );
  }, [graph.edges, selectedRoomId]);

  if (skus.length === 0) {
    return (
      <section className="panel p-4 text-[12px] text-ink-500">
        Catalog is empty. Ingest via /integrations or seed via the demo project.
      </section>
    );
  }

  return (
    <section className="panel p-3 space-y-2">
      <header className="flex items-center justify-between flex-wrap gap-2">
        <div className="eyebrow inline-flex items-center gap-1.5">
          <Tag className="w-3 h-3" /> Catalog · {skus.length} SKUs
        </div>
        <div className="text-[10.5px] text-ink-500">
          {selectedRoomId
            ? "click any SKU to add it to the selected room"
            : "select a room above to start adding"}
        </div>
      </header>
      <ul className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-2">
        {skus.map((sku) => {
          const placed = placedInSelected.has(sku.id);
          const price = attrNum(sku, "price");
          const lead = attrNum(sku, "leadDays");
          const photo = photoForSkuNode(sku);
          // The agent's pre-evaluation — informs the badge but does NOT
          // gate the click. Clicking a non-fitting SKU still goes through
          // to onAdd so the agent can emit a typed rejection Suggestion
          // (with citation) instead of silently swallowing the intent.
          const selectedRoom = selectedRoomId ? graph.nodes.find((n) => n.id === selectedRoomId && n.kind === "Room") : null;
          const fit = selectedRoom && !placed ? wouldFit(graph, selectedRoom, sku) : null;
          const blocked = fit !== null && !fit.ok;
          const softWarn = fit !== null && fit.ok && fit.soft !== undefined;
          // Only disable when there's no selection or already placed — the
          // agent now mediates "won't fit" via a typed rejection event.
          const disabled = !selectedRoomId || placed;
          return (
            <li key={sku.id}>
              <button
                type="button"
                disabled={disabled}
                draggable={!disabled}
                onDragStart={(e) => {
                  // Catalog → plan drop bridge. Drop target reads
                  // x-foyr-sku to look up the SKU and runs the same
                  // wouldFit + place flow as the click path. Drop
                  // coordinates set posX/posZ so the SKU lands where
                  // the cursor was released (CLAUDE.md §6 drag-drop
                  // is functional, not just polish).
                  if (disabled) return;
                  e.dataTransfer.setData("application/x-foyr-sku", sku.id);
                  e.dataTransfer.setData("text/plain", sku.label);
                  e.dataTransfer.effectAllowed = "copy";
                }}
                onClick={() => {
                  if (!selectedRoomId || !selectedRoom) return;
                  const verdict = fit ?? wouldFit(graph, selectedRoom, sku);
                  onAdd(sku, selectedRoomId, verdict);
                }}
                title={blocked && fit ? `${fit.reason} (click to see why the agent rejects this)` : `${sku.label} - click or drag onto the plan`}
                className={
                  "w-full text-left rounded-xl border bg-cream overflow-hidden transition group " +
                  (placed
                    ? "border-fern/40 bg-fern/5 cursor-default"
                    : blocked
                      ? "border-foyr-deep/40 bg-foyr-deep/5 hover:border-foyr-deep/70 hover:bg-foyr-deep/10 cursor-pointer"
                      : selectedRoomId
                        ? "border-border hover:border-foyr/40 hover:bg-cream-dark/40 cursor-pointer"
                        : "border-border opacity-60 cursor-not-allowed")
                }
              >
                <div className="relative h-20 bg-cream-dark">
                  <img
                    src={photo}
                    alt={sku.label}
                    className={"w-full h-full object-cover " + (blocked ? "grayscale opacity-70" : "")}
                    loading="lazy"
                    onError={(e) => {
                      // Image URL failed (e.g. 404). Replace with a neutral
                      // grey placeholder so the alt-text doesn't bleed
                      // through. CLAUDE.md §5 — surface the issue (data attr)
                      // rather than silently swallow.
                      const el = e.currentTarget;
                      el.dataset["imgFallback"] = "true";
                      el.src = "data:image/svg+xml;utf8,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 100 80'%3E%3Crect width='100' height='80' fill='%23ece7dc'/%3E%3Ctext x='50' y='44' font-family='-apple-system,Inter,sans-serif' font-size='9' fill='%237a766f' text-anchor='middle'%3Ephoto pending%3C/text%3E%3C/svg%3E";
                    }}
                  />
                  {placed ? (
                    <span className="absolute top-1 right-1 pill text-[9px] bg-fern/90 text-cream border-none">added</span>
                  ) : blocked ? (
                    <span className="absolute top-1 right-1 pill text-[9px] bg-foyr-deep text-cream border-none uppercase tracking-wider">agent rejects</span>
                  ) : softWarn ? (
                    <span className="absolute top-1 right-1 pill text-[9px] bg-foyr/80 text-cream border-none uppercase tracking-wider">unusual</span>
                  ) : selectedRoomId ? (
                    <span className="absolute top-1 right-1 grid place-items-center w-5 h-5 rounded-full bg-cream/90 text-foyr-deep group-hover:bg-foyr group-hover:text-cream transition">
                      <Plus className="w-3 h-3" />
                    </span>
                  ) : null}
                </div>
                <div className="px-2 py-1.5">
                  <div className="text-[11px] text-ink-950 font-medium leading-tight truncate">{sku.label}</div>
                  <div className="text-[10px] text-ink-500 tabular flex items-center gap-1.5 mt-0.5">
                    {lead !== null ? <span>{lead}d</span> : null}
                    {price !== null ? <span className="font-medium text-ink-950">{formatCompactMoney(price)}</span> : null}
                  </div>
                  {blocked && fit ? (
                    <div className="text-[9.5px] text-foyr-deep mt-1 leading-tight line-clamp-2" title={fit.reason}>
                      {fit.reason}
                    </div>
                  ) : softWarn && fit && fit.soft ? (
                    <div className="text-[9.5px] text-foyr/90 mt-1 leading-tight line-clamp-2" title={fit.soft}>
                      ⚠ {fit.soft}
                    </div>
                  ) : null}
                </div>
              </button>
            </li>
          );
        })}
      </ul>
    </section>
  );
}
